/**
 * Gulcan sibling picker
 *
 * Enhances server-rendered sibling links with section-based navigation.
 * Normal links remain available when JavaScript or a section request fails.
 */

(function() {
  'use strict';

  var SECTION_TARGETS = [
    { key: 'main', role: 'main' },
    { key: '17774746599fd346be', role: 'addly', activateScripts: true },
    { key: '176679661999a60f3b', role: 'selleasy', activateScripts: true },
    { key: 'gulcan_pdp_accordions_XJxVB7', role: 'accordions' },
    { key: 'product-recommendations', role: 'recommendations' },
    { key: 'gulcan_pdp_faq_DVNC7f', role: 'faq' }
  ];
  // Four covers a colour axis plus a size axis without warming a long tail.
  var IDLE_PREFETCH_LIMIT = 4;
  var sectionCache = {};
  var currentSectionPlan = null;
  var navigationSequence = 0;
  var globalEventsBound = false;

  function init() {
    var picker = document.querySelector('[data-gulcan-sibling-picker]');
    if (!picker) return;

    var data = readGroupData(closestSectionWrapper(picker, 'main') || document);
    if (!data || !data.members || !data.members.length) return;

    currentSectionPlan = discoverSectionPlan();
    updatePickerLinks(picker, data);
    rebuildSpeculationRules(data);
    rescueLegacyVariant(data);
    bindGlobalEvents();
    prefetchSiblingsWhenIdle(picker);

    var state = history.state || {};
    state.gulcanSibling = true;
    state.gulcanSiblingUrl = window.location.href;
    history.replaceState(state, '', window.location.href);
  }

  function readGroupData(scope) {
    var script = scope.querySelector('[data-sibling-group-data]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * JSON templates prefix wrapper ids with the template id, for example
   * shopify-section-template--123__main. Static sections use the shorter
   * shopify-section-main form. Section rendering can return either form.
   */
  function findSectionWrapper(scope, sectionId) {
    var exactId = 'shopify-section-' + sectionId;
    var exact = typeof scope.getElementById === 'function'
      ? scope.getElementById(exactId)
      : null;
    if (exact) return exact;

    var suffix = '__' + sectionId;
    return Array.prototype.find.call(
      scope.querySelectorAll('[id^="shopify-section-"]'),
      function(wrapper) {
        return wrapper.id.slice(-suffix.length) === suffix;
      }
    ) || null;
  }

  function closestSectionWrapper(element, sectionId) {
    var wrapper = element.closest('[id^="shopify-section-"]');
    if (!wrapper) return null;

    var exactId = 'shopify-section-' + sectionId;
    var suffix = '__' + sectionId;
    return wrapper.id === exactId || wrapper.id.slice(-suffix.length) === suffix
      ? wrapper
      : null;
  }

  /**
   * Shopify's Section Rendering API needs the complete runtime section ids.
   * JSON template keys are only used here to locate the current wrappers.
   */
  function discoverSectionPlan() {
    var sections = SECTION_TARGETS.map(function(target) {
      var wrapper = findSectionWrapper(document, target.key);
      if (!wrapper) return null;

      return {
        role: target.role,
        id: wrapper.id.replace(/^shopify-section-/, ''),
        activateScripts: target.activateScripts === true
      };
    }).filter(Boolean);
    var main = sections.find(function(section) {
      return section.role === 'main';
    });
    if (!main) return null;

    var ids = sections.map(function(section) {
      return section.id;
    });
    var chunks = [];
    for (var index = 0; index < ids.length; index += 5) {
      chunks.push(ids.slice(index, index + 5));
    }

    return {
      sections: sections,
      chunks: chunks,
      mainId: main.id,
      appIds: sections.filter(function(section) {
        return section.activateScripts;
      }).map(function(section) {
        return section.id;
      }),
      signature: ids.join(',')
    };
  }

  function getSectionPlan() {
    var planIsCurrent = currentSectionPlan && currentSectionPlan.sections.every(function(section) {
      return document.getElementById('shopify-section-' + section.id);
    });
    if (planIsCurrent) return currentSectionPlan;

    currentSectionPlan = discoverSectionPlan();
    return currentSectionPlan;
  }

  function currentMember(data) {
    var byGid = data.members.find(function(member) {
      return member.gid === data.currentProductGid;
    });
    if (byGid) return byGid;

    var path = window.location.pathname.replace(/\/$/, '');
    return data.members.find(function(member) {
      return new URL(member.url, window.location.origin).pathname.replace(/\/$/, '') === path;
    }) || data.members[0];
  }

  function axisValues(data, axisIndex) {
    var seen = {};
    var values = [];

    data.members.forEach(function(member) {
      var value = member.values && member.values[axisIndex];
      if (value == null || seen[value]) return;
      seen[value] = true;
      values.push(value);
    });

    return values;
  }

  /**
   * Primary changes first preserve every dependent value.
   * If that product does not exist, candidates are compared by each dependent
   * axis in order. The smallest value-list distance wins at the first axis
   * where candidates differ. Original member order is the final tie breaker.
   */
  function nearestPrimaryMember(data, primaryValue, sourceValues) {
    var candidates = data.members.filter(function(member) {
      return member.values && member.values[0] === primaryValue;
    });
    if (!candidates.length) return null;

    var exact = candidates.find(function(member) {
      return sourceValues.slice(1).every(function(value, offset) {
        return member.values[offset + 1] === value;
      });
    });
    if (exact) return exact;

    return candidates.map(function(member, memberIndex) {
      var distances = [];
      for (var axisIndex = 1; axisIndex < data.optionLabels.length; axisIndex += 1) {
        var values = axisValues(data, axisIndex);
        var sourceIndex = values.indexOf(sourceValues[axisIndex]);
        var memberValueIndex = values.indexOf(member.values[axisIndex]);
        if (sourceIndex < 0) sourceIndex = 0;
        if (memberValueIndex < 0) memberValueIndex = values.length;
        distances.push(Math.abs(memberValueIndex - sourceIndex));
      }
      return {
        member: member,
        memberIndex: memberIndex,
        distances: distances
      };
    }).sort(function(a, b) {
      for (var index = 0; index < a.distances.length; index += 1) {
        if (a.distances[index] !== b.distances[index]) {
          return a.distances[index] - b.distances[index];
        }
      }
      return a.memberIndex - b.memberIndex;
    })[0].member;
  }

  function exactDependentMember(data, axisIndex, optionValue, sourceValues) {
    return data.members.find(function(member) {
      if (!member.values || member.values[0] !== sourceValues[0]) return false;
      return sourceValues.every(function(value, index) {
        if (index === axisIndex) return member.values[index] === optionValue;
        return member.values[index] === value;
      });
    }) || null;
  }

  function memberForLink(link, data) {
    var source = currentMember(data);
    if (!source || !source.values) return null;

    var axisIndex = parseInt(link.dataset.axisIndex, 10);
    var optionValue = link.dataset.optionValue;
    if (axisIndex === 0) {
      return nearestPrimaryMember(data, optionValue, source.values);
    }
    return exactDependentMember(data, axisIndex, optionValue, source.values);
  }

  function updatePickerLinks(picker, data) {
    picker.querySelectorAll('[data-gulcan-sibling-link]').forEach(function(link) {
      var target = memberForLink(link, data);
      if (target) {
        link.href = target.url;
      }
    });
  }

  function bindGlobalEvents() {
    if (globalEventsBound) return;
    globalEventsBound = true;

    document.addEventListener('click', function(event) {
      var link = event.target.closest('[data-gulcan-sibling-link]');
      if (!link || link.getAttribute('aria-disabled') === 'true') return;

      var mainSection = closestSectionWrapper(link, 'main');
      if (!mainSection) return;
      var data = mainSection ? readGroupData(mainSection) : null;
      var target = data ? memberForLink(link, data) : null;
      if (!target) return;
      var sectionPlan = getSectionPlan();
      if (!sectionPlan) return;

      event.preventDefault();
      markChosenImmediately(link);
      softNavigate(target.url, true, false, sectionPlan);
    });

    document.addEventListener('pointerover', function(event) {
      var link = event.target.closest('[data-gulcan-sibling-link]');
      if (!link || (event.relatedTarget && link.contains(event.relatedTarget))) return;
      prefetchLink(link);
    });

    document.addEventListener('touchstart', function(event) {
      var link = event.target.closest('[data-gulcan-sibling-link]');
      if (link) prefetchLink(link);
    }, { passive: true });

    window.addEventListener('popstate', function(event) {
      if (event.state && event.state.gulcanSibling) {
        softNavigate(window.location.href, false, true);
      } else {
        window.location.reload();
      }
    });
  }

  /**
   * Move the selected styling onto the clicked value before anything is
   * fetched. The swap costs about 200ms of server time during which the page
   * is otherwise identical, so without this a click reads as ignored. The
   * server-rendered replacement overwrites this a moment later and stays
   * authoritative, including for any dependent axis this cannot know about.
   */
  function markChosenImmediately(link) {
    var container = link.closest('[data-sibling-values]');
    if (!container) return;

    var values = container.querySelectorAll('[data-gulcan-sibling-link]');
    for (var index = 0; index < values.length; index++) {
      values[index].classList.remove('active');
      values[index].removeAttribute('aria-current');
    }
    link.classList.add('active');
    link.setAttribute('aria-current', 'page');

    var group = link.closest('.gulcan-variant-group');
    var label = group ? group.querySelector('[data-sibling-selected-value]') : null;
    var value = link.getAttribute('data-option-value');
    if (label && value) label.textContent = value;
  }

  /**
   * Warm every sibling once the page goes idle. A section response is about
   * 26 kB over the wire, so a handful is cheap next to the ~190ms of server
   * time a cold click otherwise pays. Saver mode and 2g are excluded, and the
   * shared section cache means a later hover or click costs nothing.
   */
  function prefetchSiblingsWhenIdle(picker) {
    var connection = navigator.connection;
    var effectiveType = connection && connection.effectiveType
      ? connection.effectiveType
      : '';
    if (connection && (connection.saveData || /(^|-)2g$/.test(effectiveType))) return;

    var schedule = window.requestIdleCallback
      ? window.requestIdleCallback.bind(window)
      : function(callback) { return setTimeout(callback, 1200); };

    schedule(function() {
      var links = picker.querySelectorAll('[data-gulcan-sibling-link]');
      var warmed = 0;
      for (var index = 0; index < links.length && warmed < IDLE_PREFETCH_LIMIT; index++) {
        var link = links[index];
        if (link.getAttribute('aria-current') === 'page') continue;
        if (link.getAttribute('aria-disabled') === 'true') continue;
        prefetchLink(link);
        warmed++;
      }
    }, { timeout: 3000 });
  }

  function prefetchLink(link) {
    var connection = navigator.connection;
    var effectiveType = connection && connection.effectiveType
      ? connection.effectiveType
      : '';
    if (connection && (connection.saveData || /(^|-)2g$/.test(effectiveType))) return;

    var mainSection = closestSectionWrapper(link, 'main');
    if (!mainSection) return;
    var data = mainSection ? readGroupData(mainSection) : null;
    var target = data ? memberForLink(link, data) : null;
    var sectionPlan = getSectionPlan();
    if (target && sectionPlan) loadSections(target.url, sectionPlan).catch(function() {
      // The normal link remains the fallback.
    });
  }

  function sectionRequestUrl(targetUrl, sectionIds) {
    var url = new URL(targetUrl, window.location.origin);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.set('sections', sectionIds.join(','));
    return url.toString();
  }

  function fetchSectionChunk(targetUrl, sectionIds) {
    return fetch(sectionRequestUrl(targetUrl, sectionIds), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function(response) {
      if (!response.ok) {
        throw new Error('Section request failed');
      }
      return response.json();
    }).then(function(sections) {
      sectionIds.forEach(function(sectionId) {
        if (
          !sections ||
          typeof sections[sectionId] !== 'string' ||
          sections[sectionId].trim() === ''
        ) {
          throw new Error('Missing section: ' + sectionId);
        }
      });
      return sections;
    });
  }

  function loadSections(targetUrl, sectionPlan) {
    var canonicalUrl = new URL(targetUrl, window.location.origin);
    canonicalUrl.hash = '';
    canonicalUrl.searchParams.delete('sections');
    var cacheKey = canonicalUrl.pathname + canonicalUrl.search + '::' + sectionPlan.signature;

    if (sectionCache[cacheKey]) return sectionCache[cacheKey];

    sectionCache[cacheKey] = Promise.all(sectionPlan.chunks.map(function(sectionIds) {
      return fetchSectionChunk(canonicalUrl.toString(), sectionIds);
    })).then(function(chunks) {
      return chunks.reduce(function(allSections, chunk) {
        Object.keys(chunk).forEach(function(sectionId) {
          allSections[sectionId] = chunk[sectionId];
        });
        return allSections;
      }, {});
    }).catch(function(error) {
      delete sectionCache[cacheKey];
      throw error;
    });

    return sectionCache[cacheKey];
  }

  function parseReplacementSections(sections, sectionPlan) {
    var parser = new DOMParser();
    var replacements = {};

    sectionPlan.sections.forEach(function(section) {
      var sectionId = section.id;
      var parsed = parser.parseFromString(sections[sectionId], 'text/html');
      var wrapper = findSectionWrapper(parsed, sectionId);
      var current = findSectionWrapper(document, sectionId);
      if (!wrapper || !current) {
        throw new Error('Section wrapper missing: ' + sectionId);
      }
      replacements[sectionId] = {
        current: current,
        next: document.importNode(wrapper, true)
      };
    });

    return replacements;
  }

  function replaceSections(sections, sectionPlan) {
    var replacements = parseReplacementSections(sections, sectionPlan);

    Object.keys(replacements).forEach(function(sectionId) {
      replacements[sectionId].current.replaceWith(replacements[sectionId].next);
    });

    return replacements;
  }

  function activateAppScript(script) {
    return new Promise(function(resolve, reject) {
      var replacement = document.createElement('script');
      var type = (script.getAttribute('type') || '').toLowerCase();
      var waitsForLoad = Boolean(script.src) || type === 'module';

      Array.prototype.forEach.call(script.attributes, function(attribute) {
        replacement.setAttribute(attribute.name, attribute.value);
      });

      if (!script.src) {
        replacement.textContent = script.textContent;
      }

      if (waitsForLoad) {
        var timeout = window.setTimeout(function() {
          reject(new Error('App script timed out'));
        }, 5000);
        replacement.addEventListener('load', function() {
          window.clearTimeout(timeout);
          resolve();
        }, { once: true });
        replacement.addEventListener('error', function() {
          window.clearTimeout(timeout);
          reject(new Error('App script failed'));
        }, { once: true });
      }

      script.replaceWith(replacement);
      if (!waitsForLoad) resolve();
    });
  }

  function activateAppScripts(wrapper) {
    var scripts = Array.prototype.slice.call(wrapper.querySelectorAll('script'));
    var executable = scripts.filter(function(script) {
      var type = (script.getAttribute('type') || '').toLowerCase();
      return type !== 'application/json' && type !== 'speculationrules';
    });

    // App sections often load a dependency and then run an inline initializer.
    // Recreate scripts in source order so the initializer never races its loader.
    return executable.reduce(function(chain, script) {
      return chain.then(function() {
        return activateAppScript(script);
      });
    }, Promise.resolve());
  }

  function dispatchSectionLoad(wrapper) {
    wrapper.dispatchEvent(new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId: wrapper.id.replace('shopify-section-', '') }
    }));
  }

  function bindRatingScroll() {
    var rating = document.querySelector('.gulcan-pdp-rating');
    if (!rating || rating.dataset.siblingBound === 'true') return;
    rating.dataset.siblingBound = 'true';

    rating.addEventListener('click', function(event) {
      event.preventDefault();
      var reviews = document.getElementById('reviews');
      if (!reviews) return;

      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(function() {
        var trigger = reviews.querySelector('[data-accordion-trigger]');
        if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
          trigger.click();
        }
      }, 500);
    });
  }

  function bindFaqToggles() {
    document.querySelectorAll('[data-pdp-faq-section] [data-faq-item]').forEach(function(item) {
      var trigger = item.querySelector('[data-faq-trigger]');
      if (!trigger || trigger.dataset.siblingBound === 'true') return;
      trigger.dataset.siblingBound = 'true';

      trigger.addEventListener('click', function() {
        var shouldOpen = !item.classList.contains('open');
        item.classList.toggle('open', shouldOpen);
        trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      });
    });
  }

  function refreshJudgeMe() {
    var widget = document.querySelector('#reviews .jdgm-review-widget');
    if (!widget) return;

    if (window.jdgm && typeof window.jdgm.init === 'function') {
      try {
        window.jdgm.init();
      } catch (error) {
        // Judge.me can finish its own delayed initialization.
      }
    }
    if (window.JDGM && typeof window.JDGM.refresh === 'function') {
      try {
        window.JDGM.refresh();
      } catch (error) {
        // Judge.me can finish its own delayed initialization.
      }
    }
    widget.dispatchEvent(new CustomEvent('jdgm:refresh'));

    var reviewsTrigger = document.querySelector('#reviews [data-accordion-trigger]');
    if (reviewsTrigger && reviewsTrigger.dataset.siblingReviewBound !== 'true') {
      reviewsTrigger.dataset.siblingReviewBound = 'true';
      reviewsTrigger.addEventListener('click', function() {
        window.setTimeout(refreshJudgeMe, 100);
      });
    }
  }

  function updateDocumentState(targetUrl) {
    var mainSection = findSectionWrapper(document, 'main');
    var data = mainSection ? readGroupData(mainSection) : null;
    if (data && data.documentTitle) {
      document.title = data.documentTitle;
    }

    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = new URL(targetUrl, window.location.origin).toString();
    }
  }

  function rebuildSpeculationRules(data) {
    document.querySelectorAll('[data-gulcan-sibling-speculation]').forEach(function(script) {
      script.remove();
    });

    var urls = data.members.map(function(member) {
      return member.url;
    });
    if (!urls.length) return;

    var script = document.createElement('script');
    script.type = 'speculationrules';
    script.setAttribute('data-gulcan-sibling-speculation', '');
    script.textContent = JSON.stringify({
      prefetch: [{
        urls: urls,
        eagerness: 'moderate'
      }]
    });
    document.head.appendChild(script);
  }

  function afterSwap(replacements, targetUrl, pushHistory, sectionPlan) {
    if (pushHistory) {
      history.pushState({
        gulcanSibling: true,
        gulcanSiblingUrl: new URL(targetUrl, window.location.origin).toString()
      }, '', targetUrl);
    }

    // A replacement can introduce a different template prefix. Always rebuild
    // the plan from the wrappers that are now in the document.
    currentSectionPlan = discoverSectionPlan();
    updateDocumentState(targetUrl);

    var mainWrapper = replacements[sectionPlan.mainId].next;
    dispatchSectionLoad(mainWrapper);
    bindRatingScroll();
    bindFaqToggles();
    refreshJudgeMe();

    if (window.GulcanRecentlyViewed && typeof window.GulcanRecentlyViewed.track === 'function') {
      try {
        window.GulcanRecentlyViewed.track();
      } catch (error) {
        // Tracking never blocks product navigation.
      }
    }

    var picker = mainWrapper.querySelector('[data-gulcan-sibling-picker]');
    var data = readGroupData(mainWrapper);
    if (picker && data) {
      updatePickerLinks(picker, data);
      rebuildSpeculationRules(data);
    }
  }

  function hardNavigate(targetUrl, replaceCurrentPage) {
    if (replaceCurrentPage) {
      window.location.replace(targetUrl);
    } else {
      window.location.assign(targetUrl);
    }
  }

  function softNavigate(targetUrl, pushHistory, replaceOnFailure, requestedPlan) {
    var sequence = ++navigationSequence;
    var sectionPlan = requestedPlan || getSectionPlan();
    if (!sectionPlan) {
      hardNavigate(targetUrl, replaceOnFailure);
      return;
    }

    var currentPicker = document.querySelector('[data-gulcan-sibling-picker]');
    if (currentPicker) currentPicker.setAttribute('aria-busy', 'true');

    var preSwap = loadSections(targetUrl, sectionPlan).then(function(sections) {
      if (sequence !== navigationSequence) return null;
      return replaceSections(sections, sectionPlan);
    });

    preSwap.catch(function() {
      if (sequence !== navigationSequence) return null;
      hardNavigate(targetUrl, replaceOnFailure);
      return null;
    }).then(function(replacements) {
      if (!replacements || sequence !== navigationSequence) return null;

      // Reinitialize the main PDP synchronously after replacement. The current
      // product JSON is parsed before the new add button can be used.
      try {
        afterSwap(replacements, targetUrl, pushHistory, sectionPlan);
      } catch (error) {
        console.warn('Sibling page reinitialization failed after the section swap.', error);
        return null;
      }

      return Promise.all(sectionPlan.appIds.map(function(sectionId) {
        return activateAppScripts(replacements[sectionId].next).then(function() {
          if (sequence !== navigationSequence) return;
          dispatchSectionLoad(replacements[sectionId].next);
        }).catch(function(error) {
          // App loaders can be blocked by privacy tools. The server-rendered
          // replacement is already correct, so keep the completed navigation.
          console.warn('Sibling app section activation failed: ' + sectionId, error);
        });
      }));
    }).catch(function(error) {
      if (sequence !== navigationSequence) return;
      console.warn('Sibling post-swap enhancement failed.', error);
    }).finally(function() {
      if (sequence !== navigationSequence) return;
      var picker = document.querySelector('[data-gulcan-sibling-picker]');
      if (picker) picker.removeAttribute('aria-busy');
    });
  }

  function legacyTargetMember(data, mappedValue) {
    if (mappedValue == null) return null;

    var candidates = [];
    if (typeof mappedValue === 'object') {
      candidates = [
        mappedValue.product_gid,
        mappedValue.productGid,
        mappedValue.product_id,
        mappedValue.productId,
        mappedValue.handle,
        mappedValue.url,
        mappedValue.variant_gid,
        mappedValue.variantGid
      ];
    } else {
      candidates = [mappedValue];
    }

    return data.members.find(function(member) {
      return candidates.some(function(candidate) {
        if (candidate == null) return false;
        var value = String(candidate);
        return value === member.gid ||
          value === String(member.id) ||
          value === member.handle ||
          value === member.url ||
          value === String(member.variantId) ||
          value === 'gid://shopify/ProductVariant/' + member.variantId;
      });
    }) || null;
  }

  function rescueLegacyVariant(data) {
    var url = new URL(window.location.href);
    var variantId = url.searchParams.get('variant');
    if (!variantId || !data.legacyVariantIds) return;

    var current = currentMember(data);
    if (current && String(current.variantId) === variantId) return;

    var variantGid = variantId.indexOf('gid://') === 0
      ? variantId
      : 'gid://shopify/ProductVariant/' + variantId;
    var mappedValue = data.legacyVariantIds[variantGid];
    if (mappedValue == null) mappedValue = data.legacyVariantIds[variantId];

    var target = legacyTargetMember(data, mappedValue);
    if (!target) return;

    url.pathname = new URL(target.url, window.location.origin).pathname;
    url.searchParams.delete('variant');
    window.location.replace(url.toString());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
